/**
 * Nouveaux mails : la ligne apparaît avec les en-têtes.
 * Le corps sur disque se prépare ensuite, sans retenir la liste.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { subscribeMessageBodyCache } from "@/lib/cache/messageBodyCache";
import {
  notifyIncomingPrepare,
  type MailIncomingPreparePhase,
} from "@/lib/mail/mailIncomingPrepareEvents";
import {
  clearMailPendingReveal,
  countMailPendingRevealForAccount,
  countUnreadMailPendingRevealForAccount,
  isMailPendingReveal,
  subscribeMailPendingReveal,
} from "@/lib/mail/mailPendingReveal";
import { syncUnreadHoldsFromPreparing, clearUnreadHoldForAccount } from "@/lib/mail/mailUnreadHold";
import type { MailMessageSummary } from "@/lib/types/mail";

const HEADERS_PHASE_MS = 700;
/** 0 : la ligne suit les en-têtes. Le corps se prépare sans la masquer. */
const REVEAL_FALLBACK_MS = 0;
/** Tick UI pendant prep longue — ne révèle PAS la ligne. */
const PREPARE_TICK_MS = 900;
/** Re-tente le prime DD02→client pendant le masquage. */
const CLIENT_HYDRATE_RETRY_MS = 500;
/** Après ça, retente hydrate même si `bodyCached` liste encore faux (flag en retard). */
const HYDRATE_WITHOUT_FLAG_MS = 1_500;

function messageListKey(msg: MailMessageSummary, fallbackAccountId?: number): string {
  const accountId = msg.accountId ?? fallbackAccountId;
  if (accountId != null && msg.uid > 0) {
    return `${accountId}:${msg.uid}`;
  }
  return String(msg.outboxId ?? msg.uid);
}

function preparingByAccountKey(map: ReadonlyMap<number, number> | undefined): string {
  if (!map || map.size === 0) return "";
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([id, n]) => `${id}:${n}`)
    .join("|");
}

type Options = {
  enabled: boolean;
  scopeKey: string;
  listSettled: boolean;
  accountId?: number;
  folder?: string;
  isClientReady: (msg: MailMessageSummary) => boolean;
  clientReadyRevision: number | string;
  /** Warm DD02 tant que le mail est masqué. */
  onPrimeArrival?: (msg: MailMessageSummary) => void;
  /** DD02 prêt → hydrate client pendant le masquage (avant révélation). */
  onRevealReady?: (msg: MailMessageSummary) => void;
  accountIdForMessage?: (msg: MailMessageSummary) => number | undefined;
};

export function useInboxPreparingMessages(
  messages: MailMessageSummary[],
  options: Options
): { visibleMessages: MailMessageSummary[]; preparingCount: number } {
  const {
    enabled,
    scopeKey,
    listSettled,
    accountId,
    folder = "INBOX",
    isClientReady,
    clientReadyRevision,
    onPrimeArrival,
    onRevealReady,
    accountIdForMessage,
  } = options;

  const knownKeysRef = useRef<Set<string>>(new Set());
  const settledRef = useRef(false);
  /** Settle pendant liste vide — le 1er snapshot n’est pas une arrivée. */
  const settledWhileEmptyRef = useRef(false);
  /** Première fenêtre du dossier : visible tout de suite, pas compte-gouttes. */
  const openedWindowRef = useRef(new Set<string>());
  const scopeRef = useRef(scopeKey);
  const primedRef = useRef<Set<string>>(new Set());
  const hydrateAttemptAtRef = useRef<Map<string, number>>(new Map());
  const firstSeenAtRef = useRef<Map<string, number>>(new Map());
  const prevPreparingRef = useRef(0);
  const prevPrepAccountsRef = useRef<Set<number>>(new Set());
  const [phaseTick, setPhaseTick] = useState(0);
  const [pendingRev, setPendingRev] = useState(0);
  const [baselineRev, setBaselineRev] = useState(0);
  const [bodyCacheRev, setBodyCacheRev] = useState(0);

  if (scopeRef.current !== scopeKey) {
    scopeRef.current = scopeKey;
    knownKeysRef.current = new Set();
    settledRef.current = false;
    settledWhileEmptyRef.current = false;
    openedWindowRef.current = new Set();
    primedRef.current = new Set();
    hydrateAttemptAtRef.current = new Map();
    firstSeenAtRef.current = new Map();
    prevPreparingRef.current = 0;
    prevPrepAccountsRef.current = new Set();
  }

  const openingFolder = knownKeysRef.current.size === 0;

  // Sync : 1er snapshot après settle vide = dossier, pas arrivées (évite flash journal ×N).
  if (
    enabled &&
    settledRef.current &&
    settledWhileEmptyRef.current &&
    messages.length > 0
  ) {
    const known = knownKeysRef.current;
    for (const msg of messages) {
      if (msg.uid <= 0 && msg.sendStatus == null) continue;
      const rowAccountId = accountIdForMessage?.(msg) ?? msg.accountId ?? accountId;
      const key = messageListKey(msg, rowAccountId);
      known.add(key);
      openedWindowRef.current.add(key);
    }
    settledWhileEmptyRef.current = false;
  }

  useEffect(() => subscribeMailPendingReveal(() => setPendingRev((n) => n + 1)), []);
  useEffect(() => subscribeMessageBodyCache(() => setBodyCacheRev((n) => n + 1)), []);

  useEffect(() => {
    if (!enabled) {
      knownKeysRef.current = new Set();
      settledRef.current = false;
      settledWhileEmptyRef.current = false;
      openedWindowRef.current = new Set();
      primedRef.current = new Set();
      hydrateAttemptAtRef.current = new Map();
      firstSeenAtRef.current = new Map();
      prevPreparingRef.current = 0;
      const leaving = prevPrepAccountsRef.current;
      prevPrepAccountsRef.current = new Set();
      if (leaving.size > 0) {
        const safeLeaving = new Set(
          [...leaving].filter((id) => countMailPendingRevealForAccount(id) === 0)
        );
        if (safeLeaving.size > 0) {
          syncUnreadHoldsFromPreparing(new Map(), safeLeaving);
        }
      }
      notifyIncomingPrepare(0);
      return;
    }
    if (!listSettled) return;

    const seedKnownFromMessages = (target: Set<string>) => {
      for (const msg of messages) {
        if (msg.uid <= 0 && msg.sendStatus == null) continue;
        const rowAccountId = accountIdForMessage?.(msg) ?? msg.accountId ?? accountId;
        const key = messageListKey(msg, rowAccountId);
        const bodyFolder = msg.imapFolder ?? folder;
        target.add(key);
        openedWindowRef.current.add(key);
        if (
          rowAccountId != null &&
          msg.uid > 0 &&
          isMailPendingReveal(rowAccountId, bodyFolder, msg.uid)
        ) {
          clearMailPendingReveal(rowAccountId, bodyFolder, msg.uid);
        }
      }
    };

    if (!settledRef.current) {
      const known = new Set<string>();
      seedKnownFromMessages(known);
      knownKeysRef.current = known;
      settledRef.current = true;
      settledWhileEmptyRef.current = messages.length === 0;
      return;
    }

    if (settledWhileEmptyRef.current && messages.length > 0) {
      seedKnownFromMessages(knownKeysRef.current);
      settledWhileEmptyRef.current = false;
      setBaselineRev((n) => n + 1);
    }
  }, [
    accountId,
    accountIdForMessage,
    enabled,
    folder,
    listSettled,
    messages,
    pendingRev,
  ]);

  const result = useMemo(() => {
    if (!enabled) {
      return {
        visibleMessages: messages,
        preparingCount: 0,
        preparingByAccount: new Map<number, number>(),
        preparingUnreadByAccount: new Map<number, number>(),
        phase: "body" as MailIncomingPreparePhase,
        oldestPreparingAgeMs: 0,
      };
    }

    const known = knownKeysRef.current;
    const settled = settledRef.current;
    const primed = primedRef.current;
    const hydrateAttemptAt = hydrateAttemptAtRef.current;
    const firstSeen = firstSeenAtRef.current;
    const now = Date.now();
    let preparing = 0;
    let headersPhase = false;
    let oldestPreparingAgeMs = 0;
    const visible: MailMessageSummary[] = [];
    const toPrime: MailMessageSummary[] = [];
    const toHydrateClient: MailMessageSummary[] = [];
    const activeKeys = new Set<string>();
    const preparingByAccount = new Map<number, number>();
    const preparingUnreadByAccount = new Map<number, number>();

    for (const msg of messages) {
      const rowAccountId = accountIdForMessage?.(msg) ?? msg.accountId ?? accountId;
      const key = messageListKey(msg, rowAccountId);
      const bodyFolder = msg.imapFolder ?? folder;
      const clientCached = isClientReady(msg);
      const eligible = rowAccountId != null && msg.uid > 0 && msg.sendStatus == null;
      const pendingReveal =
        rowAccountId != null && isMailPendingReveal(rowAccountId, bodyFolder, msg.uid);

      const isNewArrival = settled && !known.has(key);
      const needsGate =
        eligible &&
        !openingFolder &&
        !openedWindowRef.current.has(key) &&
        (pendingReveal || (isNewArrival && !clientCached));

      if (!firstSeen.has(key) && needsGate && !clientCached) {
        firstSeen.set(key, now);
      }
      const age = firstSeen.has(key) ? now - (firstSeen.get(key) ?? now) : 0;
      // Truthy : PHP / merges peuvent envoyer 1 plutôt que true.
      const serverReady = Boolean(msg.bodyCached);
      // Serveur-first : DD02 prêt ⇒ ligne visible. Cache client = opportuniste.
      const stillHiding = Boolean(
        needsGate && !clientCached && !serverReady && age < REVEAL_FALLBACK_MS
      );

      if (stillHiding) {
        preparing += 1;
        oldestPreparingAgeMs = Math.max(oldestPreparingAgeMs, age);
        if (rowAccountId != null) {
          preparingByAccount.set(rowAccountId, (preparingByAccount.get(rowAccountId) ?? 0) + 1);
          if (!msg.seen) {
            preparingUnreadByAccount.set(
              rowAccountId,
              (preparingUnreadByAccount.get(rowAccountId) ?? 0) + 1
            );
          }
        }
        activeKeys.add(key);
        if (age < HEADERS_PHASE_MS) {
          headersPhase = true;
        }
        if (onPrimeArrival && !primed.has(key)) {
          primed.add(key);
          toPrime.push(msg);
        }
        // Bonus client pendant le masquage (n’empêche pas la révélation dès DD02).
        if (onRevealReady && (serverReady || age >= HYDRATE_WITHOUT_FLAG_MS)) {
          const lastTry = hydrateAttemptAt.get(key) ?? 0;
          if (now - lastTry >= CLIENT_HYDRATE_RETRY_MS) {
            hydrateAttemptAt.set(key, now);
            toHydrateClient.push(msg);
          }
        }
        continue;
      }

      firstSeen.delete(key);
      hydrateAttemptAt.delete(key);

      known.add(key);
      if (rowAccountId != null && pendingReveal) {
        clearMailPendingReveal(rowAccountId, bodyFolder, msg.uid);
      }

      visible.push(msg);
    }

    for (const key of [...firstSeen.keys()]) {
      if (!activeKeys.has(key)) {
        firstSeen.delete(key);
        hydrateAttemptAt.delete(key);
      }
    }

    if (toPrime.length > 0 && onPrimeArrival) {
      queueMicrotask(() => {
        for (const msg of toPrime) {
          onPrimeArrival(msg);
        }
      });
    }

    if (toHydrateClient.length > 0 && onRevealReady) {
      queueMicrotask(() => {
        for (const msg of toHydrateClient) {
          onRevealReady(msg);
        }
      });
    }

    const phase: MailIncomingPreparePhase =
      preparing > 0 && headersPhase ? "headers" : "body";

    return {
      visibleMessages: visible,
      preparingCount: preparing,
      preparingByAccount,
      preparingUnreadByAccount,
      phase,
      oldestPreparingAgeMs,
    };
  }, [
    accountId,
    accountIdForMessage,
    bodyCacheRev,
    clientReadyRevision,
    enabled,
    folder,
    isClientReady,
    messages,
    onPrimeArrival,
    onRevealReady,
    pendingRev,
    baselineRev,
    phaseTick,
  ]);

  const prepKey = preparingByAccountKey(result.preparingUnreadByAccount);

  useEffect(() => {
    if (!enabled) return;

    notifyIncomingPrepare(result.preparingCount, result.phase);

    const prev = prevPreparingRef.current;
    const next = result.preparingCount;
    prevPreparingRef.current = next;

    const prevAccounts = prevPrepAccountsRef.current;
    const nextAccounts = new Set(result.preparingUnreadByAccount.keys());
    const leaving = new Set<number>();
    for (const id of prevAccounts) {
      if (!nextAccounts.has(id) && countMailPendingRevealForAccount(id) === 0) {
        leaving.add(id);
      }
    }

    if (next > 0 || leaving.size > 0 || prev > 0) {
      const holdByAccount = new Map(result.preparingUnreadByAccount);
      for (const [id, n] of holdByAccount) {
        holdByAccount.set(id, Math.max(n, countUnreadMailPendingRevealForAccount(id)));
      }
      syncUnreadHoldsFromPreparing(holdByAccount, leaving);
    }
    prevPrepAccountsRef.current = nextAccounts;

    if (
      next === 0 &&
      accountId != null &&
      accountIdForMessage == null &&
      countMailPendingRevealForAccount(accountId) === 0
    ) {
      clearUnreadHoldForAccount(accountId);
    }
  }, [
    accountId,
    accountIdForMessage,
    enabled,
    pendingRev,
    prepKey,
    result.phase,
    result.preparingCount,
    result.preparingUnreadByAccount,
    scopeKey,
  ]);

  useEffect(() => {
    if (!enabled || result.preparingCount <= 0) return;

    const ms =
      result.phase === "headers" ? HEADERS_PHASE_MS + 40 : PREPARE_TICK_MS;
    const id = window.setTimeout(() => setPhaseTick((n) => n + 1), ms);
    return () => window.clearTimeout(id);
  }, [enabled, result.oldestPreparingAgeMs, result.phase, result.preparingCount]);

  return {
    visibleMessages: result.visibleMessages,
    preparingCount: result.preparingCount,
  };
}
